/**
 * Admin Wallets — V2.N.6
 *
 * Vue agrégée de TOUS les wallets suivis (monitors + portfolio) à
 * travers comptes et devices anonymes :
 *   - Distribution par chaîne
 *   - Recherche par adresse (qui suit cette adresse ?)
 *   - Top adresses partagées (suivies par plusieurs devices)
 *
 * Lit /api/admin/devices. Style Stripe-inspired light V2.L.1.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchDevices } from '../lib/fetchAdminAction';
import { relTime } from '../lib/dateHelpers';
import { adminPageStyle, adminCardStyle, adminTableStyle, adminThStyle, adminTdStyle, adminCodeStyle } from '../lib/adminStyles';
import '../layout/AdminLayout.css';

interface ChainRow { chain: string; count: number; }
interface TopWalletRow { chain: string; address: string; device_count: number; }
interface SearchRow {
  chain: string; address: string; label: string | null; source: string;
  device_id: string; user_id: string | null; last_seen: string;
}

export function Wallets() {
  const [byChain, setByChain] = useState<ChainRow[]>([]);
  const [topWallets, setTopWallets] = useState<TopWalletRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchRow[] | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [c, t] = await Promise.all([
        fetchDevices<ChainRow[]>('wallets_by_chain'),
        fetchDevices<TopWalletRow[]>('top_wallets', { limit: 30 }),
      ]);
      if (!cancelled) {
        setByChain(c || []);
        setTopWallets(t || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const doSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim().length < 3) return;
    setSearching(true);
    const r = await fetchDevices<SearchRow[]>('search_wallet', { q: query.trim() });
    setResults(r || []);
    setSearching(false);
  }, [query]);

  const totalWallets = byChain.reduce((s, r) => s + r.count, 0);

  return (
    <main style={adminPageStyle}>
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Wallets</h1>
        <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
          Tous les wallets suivis ({totalWallets}) à travers comptes et appareils anonymes.
        </div>
      </header>

      {/* Recherche */}
      <form onSubmit={doSearch} style={{ display: 'flex', gap: '8px', marginBottom: '20px', maxWidth: '560px' }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher une adresse (min 3 caractères)…"
          style={{ flex: 1, padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--card)', color: 'var(--text)', fontSize: '13px' }}
        />
        <button type="submit" disabled={query.trim().length < 3} style={{ padding: '9px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
          Rechercher
        </button>
      </form>

      {results !== null && (
        <section style={{ ...adminCardStyle, marginBottom: '20px' }}>
          <h2 style={h2Style}>Résultats ({results.length}){searching && ' …'}</h2>
          {results.length === 0 ? <Empty hint="Aucun wallet trouvé." /> : (
            <table style={adminTableStyle}>
              <thead><tr><th style={adminThStyle}>Chain</th><th style={adminThStyle}>Adresse</th><th style={adminThStyle}>Source</th><th style={adminThStyle}>Device/Compte</th><th style={adminThStyle}>Vu</th></tr></thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i}>
                    <td style={adminTdStyle}><ChainTag chain={r.chain} /></td>
                    <td style={adminTdStyle}><code style={adminCodeStyle}>{r.address}</code></td>
                    <td style={adminTdStyle}>{r.source}</td>
                    <td style={adminTdStyle}>{r.user_id ? <span style={{ color: '#0e9f6e', fontWeight: 600 }}>compte</span> : <code style={adminCodeStyle}>{r.device_id.slice(0, 10)}…</code>}</td>
                    <td style={adminTdStyle}>{relTime(r.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' }}>
        <section style={adminCardStyle}>
          <h2 style={h2Style}>Distribution par chaîne</h2>
          {loading ? <Loading /> : byChain.length === 0 ? <Empty hint="Aucun wallet mirrored." /> : (
            <table style={adminTableStyle}>
              <thead><tr><th style={adminThStyle}>Chain</th><th style={adminThStyle}>Wallets</th><th style={adminThStyle}>%</th></tr></thead>
              <tbody>
                {byChain.map((r) => (
                  <tr key={r.chain}>
                    <td style={adminTdStyle}><ChainTag chain={r.chain} /></td>
                    <td style={adminTdStyle}>{r.count}</td>
                    <td style={adminTdStyle}>{totalWallets ? ((r.count / totalWallets) * 100).toFixed(1) : '0'} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section style={adminCardStyle}>
          <h2 style={h2Style}>Adresses les plus suivies</h2>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '8px' }}>Suivies par plusieurs appareils distincts.</div>
          {loading ? <Loading /> : topWallets.length === 0 ? <Empty hint="—" /> : (
            <table style={adminTableStyle}>
              <thead><tr><th style={adminThStyle}>Chain</th><th style={adminThStyle}>Adresse</th><th style={adminThStyle}>Devices</th></tr></thead>
              <tbody>
                {topWallets.map((r, i) => (
                  <tr key={i}>
                    <td style={adminTdStyle}><ChainTag chain={r.chain} /></td>
                    <td style={adminTdStyle}><code style={adminCodeStyle}>{r.address.slice(0, 18)}…</code></td>
                    <td style={adminTdStyle}>{r.device_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </main>
  );
}

function ChainTag({ chain }: { chain: string }) {
  return <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--accent)' }}>{chain}</span>;
}
function Loading() { return <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>; }
function Empty({ hint }: { hint: string }) { return <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>{hint}</div>; }


// Style local pour les titres de section.
const h2Style: React.CSSProperties = { fontSize: '15px', fontWeight: 700, margin: 0, marginBottom: '12px' };
